import React, { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import {
  useGetProfileQuery,
  useSentRequestSupportMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import { BaseLayout } from '../../components/Layout/Layout';
import { Modal, ModalRef } from '../../components/Modal/Modal';
import { useLanguage } from '../../locale/useLanguage';
import { AuthProvider } from '../../models/auth.models';
import { Routes } from '../../routes/types';
import { DeleteModal } from './components/DeleteModal/DeleteModal';
import { FieldInfo } from './components/FieldInfo/FieldInfo';

import './ProfileView.css';
import dayjs from 'dayjs';
import { useBack } from '../../hooks/useBack';

type ProfileViewProps = {};

export const ProfileView: React.FC<ProfileViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const modalRef = useRef<ModalRef>(null);
  const { data, isLoading } = useGetProfileQuery(undefined);
  const [loading, setLoading] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [sendRequest] = useSentRequestSupportMutation();

  async function requestAccountDeletion() {
    setLoading(true);
    await sendRequest({
      message:
        '[RICHIESTA AUTOMATICA CANCELLAZIONE ACCOUNT]. Questo utente ha richiesto la cancellazione del suo account. Questo è un messaggio automatico inviato dal Backend di PDN Tracking Page.',
    }).unwrap();
    setDeleteRequested(true);
    setLoading(false);
  }

  const goBack = useBack();

  const goToProfileEdit = useCallback(() => {
    history.push(Routes.CompleteUserInfo);
  }, [history]);

  const goToChangePassword = useCallback(() => {
    history.push(Routes.ChangePassword);
  }, [history]);

  const openDeleteAccountModal = useCallback(() => {
    modalRef.current?.openModal();
  }, []);

  const closeDeleteAccountModal = useCallback(() => {
    modalRef.current?.closeModal();
  }, []);

  return (
    <BaseLayout
      onBackClick={goBack}
      hasBack
      title={t('pageTitles.profile')}
      isLoading={isLoading}
    >
      {!isLoading && data && (
        <>
          {!data.company && data.name && data.surname && (
            <FieldInfo
              label={t('profile.fullName')}
              value={`${data.name} ${data.surname}`}
            />
          )}
          {data.company && data.companyName && (
            <FieldInfo
              label={t('profile.companyName')}
              value={`${data.companyName}`}
            />
          )}
          {data.email && (
            <FieldInfo label={t('profile.email')} value={`${data.email}`} />
          )}
          {data.providers.includes(AuthProvider.Password) && (
            <FieldInfo
              label={t('profile.password')}
              value={`*******************`}
              actionLabel={t('profile.edit')}
              onAction={goToChangePassword}
            />
          )}
          {data.company && data.vatNumber && (
            <FieldInfo
              label={t('profile.vatNumber')}
              value={`${data.vatNumber}`}
            />
          )}
          {data.fiscalCode && (
            <FieldInfo
              label={t('profile.taxCode')}
              value={`${data.fiscalCode}`}
            />
          )}
          {data.company && data.legalRepresentative && (
            <FieldInfo
              label={t('profile.legalRepresentative')}
              value={`${data.legalRepresentative}`}
            />
          )}
          {!data.company && data.placeOfBirth && (
            <FieldInfo
              label={t('profile.placeOfBirth')}
              value={`${data.placeOfBirth}`}
            />
          )}
          {!data.company && data.dateOfBirth && (
            <FieldInfo
              label={t('profile.dateOfBirth')}
              value={`${dayjs(data.dateOfBirth).format('DD/MM/YYYY')}`}
            />
          )}
          <div className="profile--button-container-action">
            <Button
              isPrimary
              onClick={goToProfileEdit}
              label={
                !data.name && !data.surname && !data.companyName
                  ? t('profile.completeProfile')
                  : t('profile.editProfile')
              }
            />
          </div>
          <div className="profile--button-container-delete">
            <Button
              onClick={openDeleteAccountModal}
              label={t('profile.deleteAccount')}
            />
          </div>
          <Modal hasClose={false} ref={modalRef}>
            <DeleteModal
              onEndBenefits={requestAccountDeletion}
              onClose={async () => {
                await closeDeleteAccountModal();
                await new Promise((res) => setTimeout(res, 500));
                setDeleteRequested(false);
              }}
              onMaintainBenefits={closeDeleteAccountModal}
              deleted={deleteRequested}
              loading={loading}
            />
          </Modal>
        </>
      )}
    </BaseLayout>
  );
};
